import { apiSlice } from "../../api/apiSlice";

export const timeTrailsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Get all time trails
    getTimeTrails: builder.query({
      query: () => ({
        url: `/api/timetrials`,
        method: "GET",
      }),
      providesTags: ["TimeTrails"],
    }),

    // Get user time trails
    getUserTimeTrails: builder.query({
      query: () => {
        return `/api/timetrials/user`;
      },
      providesTags: ["UserTimeTrails"],
    }),

    // Get active time trails for popup
    getActiveTimeTrails: builder.query({
      query: () => {
        return `/api/timetrials/user/active`;
      },
      providesTags: ["ActiveTimeTrails"],
    }),

    // Get time trail by ID
    getTimeTrailById: builder.query({
      query: (arg) => {
        const _id = typeof arg === "string" ? arg : arg?._id;
        return {
          url: `/api/timetrials/${_id}`,
          method: "GET",
        };
      },
      providesTags: ["TimeTrailById"],
    }),

    // Get time trail challenges
    getTimeTrailChallenges: builder.query({
      query: (trailId) => `/api/time-trails/${trailId}/challenges`,
      providesTags: ["TimeTrailChallenges"],
    }),

    // Grouped admin time trial list (trials with pending submissions)
    getAdminTimeTrialSubmissions: builder.query({
      query: ({ search } = {}) => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        const queryString = params.toString();
        return {
          url: `/api/timetrials/admin/submissions${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      providesTags: ["AdminTimeTrialSubmissions"],
    }),

    // Submissions for a specific time trial (status tabs)
    getAdminTimeTrialSubmissionsByTrial: builder.query({
      query: ({
        timeTrialId,
        status,
        page = 1,
        limit = 10,
        search,
      } = {}) => {
        const params = new URLSearchParams();
        if (timeTrialId) params.set("timeTrialId", timeTrialId);
        if (status) params.set("status", status);
        if (page) params.set("page", String(page));
        if (limit) params.set("limit", String(limit));
        if (search) params.set("search", search);
        const queryString = params.toString();
        return {
          url: `/api/timetrials/admin/submissions${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      providesTags: ["AdminTimeTrialSubmissionsByTrial"],
    }),

    // Get time trial submissions for a specific employee (activity view)
    getEmployeeTimeTrialSubmissions: builder.query({
      query: ({ userId, status, page = 1, limit = 10 } = {}) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (page) params.set("page", page);
        if (limit) params.set("limit", limit);
        const queryString = params.toString();
        return {
          url: `/api/timetrials/admin/users/${userId}/submissions${
            queryString ? `?${queryString}` : ""
          }`,
          method: "GET",
        };
      },
      providesTags: ["EmployeeTimeTrialSubmissions"],
    }),

    // Approve time trial submission
    approveTimeTrialSubmission: builder.mutation({
      query: (timetrialId) => ({
        url: `/api/books/task/complete/${timetrialId}/approve`,
        method: "PATCH",
      }),
      invalidatesTags: [
        "AdminTimeTrialSubmissions",
        "AdminTimeTrialSubmissionsByTrial",
        "EmployeeTimeTrialSubmissions",
        "Points",
        "UserActivity",
      ],
    }),

    // Reject time trial submission
    rejectTimeTrialSubmission: builder.mutation({
      query: (timetrialId) => ({
        url: `/api/books/task/complete/${timetrialId}/reject`,
        method: "PATCH",
      }),
      invalidatesTags: [
        "AdminTimeTrialSubmissions",
        "AdminTimeTrialSubmissionsByTrial",
        "EmployeeTimeTrialSubmissions",
        "UserActivity",
      ],
    }),

    // Create time trail
    createTimeTrail: builder.mutation({
      query: ({ createTimeTrailBody }) => {
        return {
          url: "/api/timetrials",
          method: "POST",
          body: createTimeTrailBody,
        };
      },
      invalidatesTags: ["TimeTrails"],
    }),

    // Update time trail
    updateTimeTrail: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/timetrials/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["TimeTrails", "TimeTrailById"],
    }),

    // Delete time trail
    deleteTimeTrail: builder.mutation({
      query: ({ trailId }) => ({
        url: `/api/timetrials/${trailId}`,
        method: "POST",
      }),
      invalidatesTags: ["TimeTrails"],
    }),

    // Start time trail
    startTimeTrail: builder.mutation({
      query: (trailId) => ({
        url: `/api/time-trails/${trailId}/start`,
        method: "POST",
      }),
      invalidatesTags: ["TimeTrailById", "TimeTrails"],
    }),

    // Complete time trail challenge
    completeChallenge: builder.mutation({
      query: ({ trailId, challengeId, data }) => ({
        url: `/api/time-trails/${trailId}/challenges/${challengeId}/complete`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "TimeTrailById",
        "TimeTrailChallenges",
        "Points",
        "UserActivity",
      ],
    }),

    // Create time trail submission
    createTimeTrailSubmission: builder.mutation({
      query: ({ timetrialId, submissionData }) => {
        // Check if submissionData is FormData (file upload) or regular object (URL submission)
        const isFormData = submissionData instanceof FormData;

        return {
          url: `/api/timetrials/${timetrialId}/submission`,
          method: "POST",
          body: submissionData,
          // Don't set Content-Type for FormData, let the browser set it with boundary
          ...(isFormData
            ? {}
            : { headers: { "Content-Type": "application/json" } }),
        };
      },
      invalidatesTags: ["TimeTrails", "TimeTrailById", "UserActivity"],
    }),

    // Get user progress for time trails
    getUserTimeTrailProgress: builder.query({
      query: () => `/api/time-trails/user/progress`,
      providesTags: ["UserTimeTrailProgress"],
    }),
  }),
});

export const {
  useGetTimeTrailsQuery,
  useGetUserTimeTrailsQuery,
  useGetActiveTimeTrailsQuery,
  useGetTimeTrailByIdQuery,
  useGetTimeTrailChallengesQuery,
  useGetAdminTimeTrialSubmissionsQuery,
  useGetAdminTimeTrialSubmissionsByTrialQuery,
  useGetEmployeeTimeTrialSubmissionsQuery,
  useApproveTimeTrialSubmissionMutation,
  useRejectTimeTrialSubmissionMutation,
  useCreateTimeTrailMutation,
  useUpdateTimeTrailMutation,
  useDeleteTimeTrailMutation,
  useStartTimeTrailMutation,
  useCompleteChallengeMutation,
  useCreateTimeTrailSubmissionMutation,
  useGetUserTimeTrailProgressQuery,
} = timeTrailsApiSlice;
